// ============================================================================
// JoinWithCode.jsx — "Kodi i Familjes": mekanizmi i tretë i bashkimit, përveç
// ftesës me email (AcceptInvite.jsx) dhe krijimit të familjes së re
// (CreateFamily.jsx). Krejt i ndarë, si ato dy — Auth i vetin, s'ndan
// gjendje me App.jsx.
//
// Rruga: /join?code=XXXXXX (shih main.jsx). Kodi mund të vijë nga URL-ja
// (link i gatshëm i kopjuar nga admini) ose të shkruhet me dorë.
//
// Rrjedha:
//   1. Kërkon sesion Auth (Google/Apple/Email) — askush s'e provon kodin
//      anonimisht (edhe për arsye kufizimi provash, shih schema.sql).
//   2. rpc_resolve_join_code(kodi) → "ky kod i përket familjes X".
//   3. Kërkon veten me emër+mbiemër → rpc_search_people_for_code_join.
//        - Një ose disa përputhje → zgjedh veten (dallohet me emrat e
//          prindërve/datëlindjen, nëse ka disa me të njëjtin emër).
//        - Zero përputhje → NDAL. Shih rregullin më poshtë.
//   4. Sukses → çdo bashkim fillon me rol "Shikues" (Viewer) — admini e
//      shqyrton dhe e ngre rolin me dorë kur të bindet kush është personi.
//
// RREGULLI (1 shtator 2026) — kodi NUK KRIJON persona, vetëm LIDH:
//
//   Vetëm një person në platformë mund të ekzistojë pa prindër dhe pa lidhje:
//   paraardhësi i parë. Askush tjetër.
//
//   Prandaj rruga "S'jam në listë → fillo degën time" u hoq. Ajo krijonte një
//   person me `parent_ids = '{}'` — pa prind, pa fëmijë, pa martesë — i
//   padukshëm në pemë përgjithmonë. Njerëz që s'e gjenin veten shkruanin një
//   emër tjetër dhe përfundonin jashtë pemës.
//
//   Në një pemë familjare, personi përcaktohet nga LIDHJET, jo nga emri. Një
//   nyje pa brinjë nuk është anëtar; është kartelë që noton.
//
//   Radha e drejtë është: admini e krijon personin në pemë, në vendin e tij
//   të saktë → e fton → personi hyn.
//
//   Serveri e zbaton këtë vetë (`KODI1_dera_mbyllet.sql`); heqja këtu është
//   ana e dukshme e së njëjtës gjë.
//
// KODI PERSONAL `P-…` (KODI2) — mekanizmi i katërt:
//   Admini klikon mbi profilin e një personi dhe gjeneron një kod vetëm për të.
//   Personi hyn dhe NUK shkruan asnjë emër: kodi e di kush është. Kjo e heq
//   nga rrënja shkakun e problemit ("nuk e gjej veten, shkruaj emër tjetër").
//   Një përdorim, 7 ditë, i revokueshëm.
// ============================================================================

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next"; // 6 shtator 2026 — mungonte: faqja binte me «useTranslation is not defined»
import { supabase } from "./supabaseClient";

import { shenoGjurmen as shenoProven } from "./gjurma.js"; // PROVAT1 — regjistri i hapave (gjurma e derës jeton te HyrjaEJashtme)
import HyrjaEJashtme, { KornizaEHyrjes, useTokenatEHyrjes, RUAJTJA_E_KODIT } from "./HyrjaEJashtme.jsx"; // e njëjta ballinë, para dhe pas hyrjes

// KARTELA1 — vetëm fshati/qyteti: «Vajnik, Skenderaj, Kosovë» → «Vajnik».
// Kaq i duhet njeriut për ta njohur kushëririn; pjesa tjetër është zhurmë.
function vendiIShkurter(v) {
  return String(v || "").split(",")[0].trim();
}

export default function JoinWithCode() {
  useEffect(() => { shenoProven("join_page", null, (typeof document !== "undefined" && document.referrer) || null, true, null); }, []);
  // 4 shtator 2026 — kjo faqe hapet nga një kod që dikush ta dërgoi, pra shpesh
  // pa e hapur kurrë aplikacionin. Ishte krejt shqip e ngulitur.
  const { t } = useTranslation();
  // PAMJA E BALLINËS (12 shtator 2026) — ngjyrat vijnë nga varianti i ekranit
  // të hyrjes, jo nga një paletë e vetë kësaj faqeje. Emrat e vjetër (bosht,
  // dyte, kufi…) mbeten, që rregullat e kontrastit të mos preken.
  const { tk } = useTokenatEHyrjes();
  const P = useMemo(() => paleta(tk), [tk]);
  useEffect(() => { try { sessionStorage.removeItem(RUAJTJA_E_KODIT); } catch (_e) { /* asgjë */ } }, []);
  // Kodi nga URL-ja mund të jetë kod FAMILJE (6 shifra) ose kod NDERI (fillon
  // me "H-", alfanumerik) — shih FAZA5_honorary.sql. Kodet e nderit shfaqin
  // VETËM profilin e vet dhe s'kalojnë nëpër kërkimin e familjes.
  const _rawParamCode = ((new URLSearchParams(window.location.search).get("code") || "").trim()
    // pas kthimit nga Google/Apple kodi s'është më te adresa — HyrjaEJashtme e ruajti
    || (() => { try { return (sessionStorage.getItem(RUAJTJA_E_KODIT) || "").trim(); } catch (_e) { return ""; } })());
  const _isHonoraryCode = (v) => /^H-/i.test((v || "").trim());
  // KODI2 — kodi personal ndjek të njëjtën konventë prefiksi si ai i mysafirit.
  // Falë saj asnjë funksion ekzistues nuk u prek: kodet personale kanë RPC-të
  // e veta, dhe `rpc_resolve_join_code` mbeti fjalë për fjalë siç ishte.
  const _isPersonCode = (v) => /^P-/i.test((v || "").trim());
  const initialCode = (_isHonoraryCode(_rawParamCode) || _isPersonCode(_rawParamCode))
    ? _rawParamCode.toUpperCase()
    : _rawParamCode.replace(/\D/g, "").slice(0, 6);

  const [session, setSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);


  const [code, setCode] = useState(initialCode);
  const [family, setFamily] = useState(null); // {family_id, family_name, family_slug}
  const [codeError, setCodeError] = useState("");
  const [codeBusy, setCodeBusy] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [candidates, setCandidates] = useState(null); // null = ende s'ka kërkuar
  // Kërkesë e userit — "për ta bërë më profesional", mos e mbaj listën
  // gjithmonë të ngarkuar me prindër+bashkëshort+fëmijë për të gjithë. Meqë
  // vetë kërkimi (rpc_search_people_for_code_join) filtron pikërisht sipas
  // emrit, çdo rezultat i kthyer TASHMË ka emër identik me të tjerët — pra
  // "përplasje" do të thotë thjesht candidates.length > 1. Llogarit NIVELIN
  // MINIMAL të informacionit shtesë (0=asnjë, 1=+prindër, 2=+bashkëshort,
  // 3=+fëmijë) që mjafton për t'i dalluar TË GJITHË kandidatët mes tyre —
  // dhe shfaq VETËM aq, jo gjithmonë gjithçka.
  // KARTELA1 (13 shtator 2026) — pronari: «në raste normale vetëm emri; kur dy
  // kanë të njëjtin emër, viti · vendi dhe prindërit; kur edhe këto janë të
  // njëjta, bashkëshorti; pastaj fëmijët». Mjaft për të mos gabuar, pa
  // bombardim.
  //   0 = vetëm emri (një kandidat)
  //   1 = + «lindur 1973 · Vajnik» + «prindër: …»
  //   2 = + bashkëshorti/ja
  //   3 = + fëmijët
  const disambigLevel = useMemo(() => {
    if (!candidates || candidates.length <= 1) return 0;
    const keyAtLevel = (c, level) => {
      let k = "";
      if (level >= 1) k += "|b:" + String(c.birth_date || "").slice(0, 4) + "|v:" + vendiIShkurter(c.birth_place) + "|p:" + (c.parent_names || "");
      if (level >= 2) k += "|s:" + (c.spouse_names || "");
      if (level >= 3) k += "|c:" + (c.children_names || "");
      return k;
    };
    for (let level = 1; level <= 3; level++) {
      const keys = candidates.map((c) => keyAtLevel(c, level));
      if (new Set(keys).size === candidates.length) return level;
    }
    return 3; // s'u arrit dallim i plotë as me gjithçka — shfaq maksimumin çfarë ka
  }, [candidates]);

  // NJË FUSHË PËR TRI KODET (1 shtator 2026).
  //
  // Më parë fusha i hidhte poshtë të gjitha shkronjat (`replace(/\D/g, "")`),
  // ndaj `H-` dhe `P-` nuk shkruheshin dot aty — dhe pikërisht prandaj u deshën
  // dy lidhje të fshehura me shkronja 12.5px që kalonin rreth saj. Ato dukeshin
  // informacion, jo opsion, dhe kush nuk i lexonte me kujdes i kalonte.
  //
  // Tani fusha i pranon të tria format, dhe lidhjet u hoqën bashkë me katër
  // gjendje e dy forma. Zgjidhja HEQ kod, nuk shton.
  //
  // `dorezuar` ekziston që ekrani të mos ndërrohet në mes të shkrimit: sapo
  // shkruaje "H-", `_isHonoraryCode` bëhet i vërtetë dhe faqja do të kërcente
  // para se ta mbaroje kodin. Tani ndërrohet vetëm kur e dorëzon — ose kur kodi
  // vjen i plotë nga URL-ja.
  const [dorezuar, setDorezuar] = useState(
    _isHonoraryCode(initialCode) || _isPersonCode(initialCode)
  );

  // KODI2 — kod personal (P-…): as kërkim, as emër. Kodi e di kush je.
  const isPerson = dorezuar && _isPersonCode(code);
  const [personInfo, setPersonInfo] = useState(null);   // { person_id, emri, familja }
  const [personBusy, setPersonBusy] = useState(false);
  const [personError, setPersonError] = useState("");

  const [claimBusy, setClaimBusy] = useState(false);
  const [claimError, setClaimError] = useState("");
  // 12 shtator 2026 — ekrani «U krye! → Hape pemën» u hoq: pronari, «pasi
  // klikon te emri i vet duhet të futet automatikisht te familja, s'ka pse
  // pyetet edhe një herë». Pas lidhjes shkohet drejt te pema. `hyrjaNeFamilje`
  // mban vetëm gjendjen «duke hyrë…» derisa faqja të ndërrohet.
  const [hyrjaNeFamilje, setHyrjaNeFamilje] = useState(false);
  const shkoTeFamilja = (slug) => {
    setHyrjaNeFamilje(true);
    try { if (slug) localStorage.setItem("pef_family_slug", slug); } catch (_e) { /* modalitet privat */ }
    window.location.replace(slug ? `/f/${slug}` : "/");
  };

  // FAZA 5 — kod mysafiri (H-…): fluks i veçantë, s'kërkon kërkim familjeje.
  const isHonorary = dorezuar && _isHonoraryCode(code);
  const [honoraryBusy, setHonoraryBusy] = useState(false);
  const [honoraryError, setHonoraryError] = useState("");

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setSessionLoading(false);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => setSession(newSession));
    return () => listener.subscription.unsubscribe();
  }, []);

  // Sapo të ketë sesion DHE një kod 6-shifror gati (nga URL ose i shkruar),
  // provo ta zgjidhë automatikisht.
  useEffect(() => {
    if (!session || family || code.length !== 6) return;
    resolveCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, code]);

  // Hyrja (Google/Apple/email) jeton te HyrjaEJashtme — e njëjta si ballina (12 shtator 2026).

  async function resolveCode() {
    setCodeError("");
    setCodeBusy(true);
    const { data, error } = await supabase.rpc("rpc_resolve_join_code", { p_code: code });
    setCodeBusy(false);
    if (error) { setCodeError(error.message); shenoProven("resolve_code", code, null, false, error.message); return; }
    const row = Array.isArray(data) ? data[0] : data;
    if (!row || !row.valid) { setCodeError(t("This code has expired, has been revoked, or does not exist.")); shenoProven("resolve_code", code, null, false, "invalid or expired code"); return; }
    shenoProven("resolve_code", code, null, true, row.family_name || null, row.family_id);
    setFamily(row);
  }

  async function handleSearch(e) {
    e.preventDefault();
    setSearchError("");
    if (!firstName.trim()) { setSearchError(t("Write at least the first name.")); return; }
    setSearchBusy(true);
    const { data, error } = await supabase.rpc("rpc_search_people_for_code_join", {
      p_family_id: family.family_id,
      p_first_name: firstName.trim(),
      p_last_name: lastName.trim(),
    });
    setSearchBusy(false);
    const kerkuar = `${firstName.trim()} ${lastName.trim()}`.trim();
    if (error) { setSearchError(error.message); shenoProven("search", code, kerkuar, false, error.message, family.family_id); return; }
    shenoProven("search", code, kerkuar, true, `${(data || []).length} found`, family.family_id);
    setCandidates(data || []);
  }

  async function claimPerson(personId, name) {
    setClaimError("");
    setClaimBusy(true);
    const { error } = await supabase.rpc("rpc_claim_person_via_code", { p_code: code, p_person_id: personId });
    setClaimBusy(false);
    if (error) { setClaimError(error.message); shenoProven("claim", code, name, false, error.message, family && family.family_id); return; }
    shenoProven("claim", code, name, true, null, family && family.family_id);
    shkoTeFamilja(family && family.family_slug);
  }

  // FAZA 5 — shpengon kodin e nderit: lidh llogarinë VETËM me profilin honorary
  // dhe s'shfaq persona të tjerë. Serveri (rpc_redeem_honorary_code) ndalon
  // bashkimin para datës së fillimit të aksesit.
  async function redeemHonorary(e) {
    if (e) e.preventDefault();
    setHonoraryError("");
    const c = (code || "").trim();
    if (!_isHonoraryCode(c)) { setHonoraryError(t("Invalid guest code (it must start with “H-”).")); return; }
    setHonoraryBusy(true);
    const { data: pid, error } = await supabase.rpc("rpc_redeem_honorary_code", { p_code: c });
    if (error) { setHonoraryBusy(false); setHonoraryError(error.message); shenoProven("redeem_honorary", c, null, false, error.message); return; }
    shenoProven("redeem_honorary", c, null, true, null);
    void pid;
    shkoTeFamilja(null); // profili i mysafirit — ballina e gjen vetë familjen e lidhur
  }

  // KODI2 — lexon kodin personal dhe tregon KË do të lidhë, para se të lidhë.
  // Konfirmimi me emër është e gjithë poenta: personi nuk shkruan asgjë, vetëm
  // sheh «Ti je X te Familja Y» dhe pranon.
  async function resolvePersonCode(c) {
    const v = (c || "").trim().toUpperCase();
    if (!v) return;
    setPersonBusy(true); setPersonError(""); setPersonInfo(null);
    const { data, error } = await supabase.rpc("rpc_resolve_person_code", { p_code: v });
    setPersonBusy(false);
    if (error) { setPersonError(error.message); shenoProven("resolve_person", v, null, false, error.message); return; }
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) { setPersonError(t("The code is not valid or has expired.")); shenoProven("resolve_person", v, null, false, "invalid or expired code"); return; }
    shenoProven("resolve_person", v, null, true, row.emri || null, row.family_id || null);
    setPersonInfo(row);
  }

  async function claimPersonCode() {
    setPersonBusy(true); setPersonError("");
    const { error } = await supabase.rpc("rpc_claim_via_person_code", { p_code: code.trim().toUpperCase() });
    setPersonBusy(false);
    if (error) { setPersonError(error.message); shenoProven("claim_person", code.trim().toUpperCase(), personInfo?.emri || null, false, error.message); return; }
    shenoProven("claim_person", code.trim().toUpperCase(), personInfo?.emri || null, true, null);
    shkoTeFamilja(null); // kodi personal s'e sjell slug-un — ballina e gjen vetë familjen e lidhur
  }

  // Sapo kodi merr formën `P-…`, lexoje vetvetiu — përndryshe do të duhej një
  // klikim i tepërt vetëm për të parë emrin.
  useEffect(() => {
    if (session && isPerson && !personInfo && !personBusy && !personError) resolvePersonCode(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, isPerson, code]);

  // ---------- render ----------

  if (sessionLoading) return <KornizaEHyrjes titull="Duke ngarkuar…">…</KornizaEHyrjes>;

  if (hyrjaNeFamilje) return <KornizaEHyrjes titull={t("Done! ✓")}>{t("Opening the family tree…")}</KornizaEHyrjes>;

  // E NJËJTA DERË SI BALLINA — stema, ilustrimi, Google/Apple/email; kodi
  // ruhet te sessionStorage para OAuth-it dhe lexohet lart, te `_rawParamCode`.
  if (!session) {
    return <HyrjaEJashtme celesiTitullit="hyrje.joinTitle" celesiHyrjes="hyrje.joinIntro" />;
  }

  // FAZA 5 — kod mysafiri (nga URL ose i shkruar): kartë e veçantë, pa kërkim familjeje.
  if (isHonorary) {
    return (
      <KornizaEHyrjes titull={t("Guest code")}>
        <p style={{ marginBottom: 16, fontSize: 14, color: P.dyte }}>{t("This is a guest code. Confirm to link your account with the matching profile. You will only have access to that profile.")}</p>
        <div style={{ fontFamily: "monospace", fontSize: 16, letterSpacing: 2, marginBottom: 16, textAlign: "center" }}>{code}</div>
        {honoraryError && <div style={{ color: "#b91c1c", fontSize: 13, marginBottom: 12 }}>{honoraryError}</div>}
        <button onClick={redeemHonorary} disabled={honoraryBusy} style={P.button}>{honoraryBusy ? t("Linking…") : t("Link the account")}</button>
        <button type="button" onClick={() => { setCode(""); setHonoraryError(""); setDorezuar(false); }} style={{ ...P.oauth, border: "none", marginTop: 8 }}>← {t("I have a different code")}</button>
      </KornizaEHyrjes>
    );
  }

  // KODI2 — kod personal: pa kërkim, pa emër për të shkruar. Vetëm konfirmim.
  if (isPerson) {
    return (
      <KornizaEHyrjes titull={t("Personal code")}>
        {personBusy && !personInfo ? (
          <p style={{ fontSize: 14, color: P.dyte }}>{t("Checking the code…")}</p>
        ) : personInfo ? (
          <>
            <p style={{ marginBottom: 16, fontSize: 14, color: P.dyte }}>
              {t("This code links you with a profile that already exists in the tree. Check the name and confirm.")}
            </p>
            <div style={{ border: `1px solid ${P.kufi}`, borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 2 }}>{personInfo.emri}</div>
              <div style={{ fontSize: 14, color: P.dyte }}>{personInfo.familja}</div>
            </div>
            {personError && <div style={{ color: "#b91c1c", fontSize: 13, marginBottom: 12 }}>{personError}</div>}
            <button onClick={claimPersonCode} disabled={personBusy} style={P.button}>
              {personBusy ? t("Linking…") : t("Yes, this is me — link the account")}
            </button>
            <p style={{ fontSize: 12.5, color: P.trete, marginTop: 10, textAlign: "center" }}>
              {t("Not you? Do not use the code, and tell whoever sent it.")}
            </p>
          </>
        ) : (
          <>
            <div style={{ fontFamily: "monospace", fontSize: 16, letterSpacing: 2, marginBottom: 16, textAlign: "center" }}>{code}</div>
            {personError && <div style={{ color: "#b91c1c", fontSize: 13, marginBottom: 12 }}>{personError}</div>}
            <button onClick={() => { setPersonError(""); resolvePersonCode(code); }} disabled={personBusy} style={P.button}>{t("Try again")}</button>
          </>
        )}
        <button type="button" onClick={() => { setCode(""); setPersonError(""); setPersonInfo(null); setDorezuar(false); }}
          style={{ ...P.oauth, border: "none", marginTop: 8 }}>← {t("I have a different code")}</button>
      </KornizaEHyrjes>
    );
  }

  if (!family) {
    // NJË FUSHË E VETME PËR TRI KODET.
    //
    // Më parë kjo faqe kishte një fushë vetëm-numra dhe DY lidhje të fshehura
    // me shkronja 12.5px poshtë saj. Faqja kishte tri rrugë hyrjeje, por vetëm
    // njërën e prezantonte si rrugë hyrjeje — dy të tjerat rrinin si fusnota,
    // dhe kush nuk i lexonte me kujdes i kalonte. (Rregulli 5.)
    //
    // Meqë kodi tashmë e njeh llojin nga forma, ndërfaqja thjesht duhet t'i
    // besojë asaj: një fushë, dhe kodi thotë vetë ku të shkojë. Bashkë me
    // lidhjet u hoqën katër gjendje dhe dy forma të tëra.
    const gati = kodiEshteGati(code);
    const vetemShifra = /^\d*$/.test(code);
    return (
      <KornizaEHyrjes titull={t("Enter the code")}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!gati) return;
            // Kodi i familjes zgjidhet menjëherë; ata me prefiks vetëm
            // ndërrojnë ekranin, sepse kanë RPC-të e veta.
            if (/^\d{6}$/.test(code)) resolveCode(); else setDorezuar(true);
          }}
          style={{ display: "flex", flexDirection: "column", gap: 12 }}
        >
          <input
            value={code}
            onChange={(e) => setCode(normalizoKodin(e.target.value))}
            placeholder="000000"
            // Tastiera numerike ruhet për rastin e zakonshëm — kodin
            // 6-shifror — dhe kalon te ajo e zakonshmja sapo shfaqet një
            // shkronjë. Nëntëdhjetë përqind e njerëzve nuk e vërejnë fare.
            inputMode={vetemShifra ? "numeric" : "text"}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            required
            style={{
              ...P.input,
              fontSize: 24,
              letterSpacing: vetemShifra ? 6 : 2,
              textAlign: "center",
              fontFamily: "monospace",
            }}
          />
          {codeError && <div style={{ color: "#B91C1C", fontSize: 13.5 }}>{codeError}</div>}
          <button type="submit" disabled={codeBusy || !gati} style={{ ...P.button, opacity: (codeBusy || !gati) ? 0.55 : 1 }}>
            {codeBusy ? t("Checking…") : t("Continue")}
          </button>
        </form>

        {/* Informacion që ka të drejtë të duket informacion — sepse ËSHTË
            informacion, jo një opsion i maskuar si fusnotë. */}
        <p style={{ marginTop: 14, fontSize: 13, color: P.trete, lineHeight: 1.55, textAlign: "center" }}>
          {t("The family code has 6 digits. A personal code starts with")} <strong style={{ color: P.dyte }}>P-</strong>,
          {t("a guest code with")} <strong style={{ color: P.dyte }}>H-</strong>. {t("Enter any of them here.")}
        </p>
      </KornizaEHyrjes>
    );
  }

  return (
    <KornizaEHyrjes titull={t("You are joining {{familja}}", { familja: family.family_name })}>
      {candidates === null ? (
        <form onSubmit={handleSearch} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <p style={{ fontSize: 14, color: P.dyte, marginBottom: 4 }}>{t("Find yourself in the tree by name, so we can link your account with the profile that already exists.")}</p>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}><Label>{t("First name")}</Label><input value={firstName} onChange={(e) => setFirstName(e.target.value)} required style={P.input} /></div>
            <div style={{ flex: 1 }}><Label>{t("Last name")}</Label><input value={lastName} onChange={(e) => setLastName(e.target.value)} style={P.input} /></div>
          </div>
          {searchError && <div style={{ color: "#b91c1c", fontSize: 13 }}>{searchError}</div>}
          <button type="submit" disabled={searchBusy} style={P.button}>{searchBusy ? t("Searching…") : t("Search")}</button>
        </form>
      ) : (
        <div>
          {candidates.length === 0 ? (
            <p style={{ fontSize: 14, color: P.dyte, marginBottom: 16 }}>{t("Nobody with that name was found in this family.")}</p>
          ) : (
            <>
              {/* Etiketa duhet t'i thotë të vërtetën situatës.
                  Me NJË rezultat nuk ka listë, ndaj «Zgjidh veten nga lista»
                  ishte gënjeshtër e vogël — dhe pikërisht ajo e shtonte
                  hamendjen: njeriu nuk e dinte a ishte ky rezultati i kërkimit
                  të tij apo diçka për të plotësuar. */}
              <p style={{ fontSize: 14, color: P.dyte, marginBottom: 10, lineHeight: 1.5 }}>
                {candidates.length === 1
                  ? t("This is what we found for you. Is this you?")
                  : t("Several people share the same name — tell yourself apart using the extra details:")}
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                {candidates.map((c) => {
                  const emriPlote = `${c.first_name || ""} ${c.last_name || ""}`.trim();
                  // Rreshti mbështetës mblidhet një herë, që kartela ta ketë
                  // gjithmonë një — edhe kur s'ka përplasje emrash. Pa të,
                  // kartela mbetej një emër i vetëm në një kuti, dhe një emër
                  // në një kuti është pikërisht ajo që duket fushë teksti.
                  // KODI9 (13 shtator 2026) — vitet vijnë TË PARAT: «lindur 1950 ·
                  // vdiq 1998 · prindër: …». Pronari: dy «Imer» me të njëjtët
                  // prindër, njëri i ndjerë — rreshti pritej te emrat e prindërve
                  // dhe asgjë s'i dallonte. Viti (jo data e plotë) — kaq mban
                  // mend njeriu për një kushëri. `death_date` e kthen KODI9.
                  const viti = (d) => (d ? String(d).slice(0, 4) : "");
                  // KARTELA1 — rreshtat mbështetës, sipas nivelit (shih disambigLevel):
                  //   2: «lindur 1973 · Vajnik»   3: «prindër: …»   4: «bashkëshort: …» / «prind i: …»
                  // Me një kandidat s'ka asnjë rresht — vetëm emri dhe «Ky jam unë».
                  const rreshtat = disambigLevel === 0 ? [] : [
                    [c.birth_date ? t("born {{x}}", { x: viti(c.birth_date) }) : null,
                     c.death_date ? t("died {{x}}", { x: viti(c.death_date) }) : null,
                     vendiIShkurter(c.birth_place) || null].filter(Boolean).join(" · ") || null,
                    c.parent_names ? t("parents: {{x}}", { x: c.parent_names }) : t("no parents recorded"),
                    disambigLevel >= 2 && c.spouse_names ? t("spouse: {{x}}", { x: c.spouse_names }) : null,
                    disambigLevel >= 3 && c.children_names ? t("parent of: {{x}}", { x: c.children_names }) : null,
                  ].filter(Boolean);

                  return (
                    <button
                      key={c.person_id}
                      onClick={() => claimPerson(c.person_id, emriPlote)}
                      disabled={claimBusy}
                      // KARTELË NJERIU, JO FUSHË (rregulli 4).
                      //
                      // Më parë ky buton përdorte `oauthButtonStyle`, i cili
                      // kishte TË NJËJTIN kufi `1px solid #d1d5db` dhe të njëjtin
                      // sfond të bardhë si `inputStyle`. Kandidati dhe fusha e
                      // kërkimit ishin, fjalë për fjalë, i njëjti objekt vizual —
                      // prandaj njeriu nuk e dallonte dot rezultatin nga fusha.
                      //
                      // Tani: sipërfaqe e mbushur, vijë theksi majtas, rreth me
                      // shkronjë dhe emër i plotë në kontrast. Truri e klasifikon
                      // menjëherë si «dikush», jo si «diçka për të shkruar».
                      style={{
                        display: "flex", alignItems: "flex-start", gap: 12, width: "100%",
                        textAlign: "left", padding: "12px 14px", borderRadius: 10,
                        background: P.mbush,
                        border: `1px solid ${P.mbushKufi}`,
                        boxShadow: `inset 3px 0 0 ${P.ok}`,
                        cursor: claimBusy ? "default" : "pointer",
                        opacity: claimBusy ? 0.6 : 1,
                        minHeight: 60,
                      }}
                    >
                      <span aria-hidden="true" style={{
                        width: 40, height: 40, borderRadius: 999, flexShrink: 0,
                        background: ngjyraEAvatarit(c.person_id || emriPlote),
                        color: "#fff", fontSize: 17, fontWeight: 700,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>{inicialja(c.first_name || c.last_name)}</span>

                      {/* KARTELA1 — rreshti 1: emri + «Ky jam unë» në një linjë;
                          rreshtat mbështetës poshtë, me gjerësi të plotë dhe pa
                          «…» — emrat e prindërve duhen lexuar deri në fund, se
                          pikërisht ata e bëjnë dallimin. */}
                      <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 40 }}>
                          <span style={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: 700, color: P.bosht, lineHeight: 1.25,
                                         overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {emriPlote}
                          </span>
                          {/* Fjalët e veprimit brenda kartelës — i njëjti formulim
                              si te ekrani i kodit personal, që të dyja rrugët të
                              ndihen si i njëjti produkt. */}
                          <span style={{
                            flexShrink: 0, fontSize: 12.5, fontWeight: 700, color: P.ok,
                            display: "flex", alignItems: "center", gap: 4,
                          }}>{t("This is me")} <span aria-hidden="true">→</span></span>
                        </span>
                        {rreshtat.map((r, i) => (
                          <span key={i} style={{ fontSize: 12.5, color: P.trete, lineHeight: 1.4, overflowWrap: "anywhere" }}>{r}</span>
                        ))}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {claimError && <div style={{ color: "#b91c1c", fontSize: 13, marginBottom: 12 }}>{claimError}</div>}

          {/* Ky ishte butoni «S'jam në listë — fillo degën time». U hoq: ai
              krijonte një person pa asnjë lidhje, të padukshëm në pemë.
              Në vend të tij, udhëzimi i saktë — sepse personi duhet të
              ekzistojë në pemë PARA se të hyjë, jo pasi të ketë hyrë. */}
          {candidates.length === 0 && (
            <div style={{ background: P.mbush, border: `1px solid ${P.kufi}`, borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{t("You are not in the tree yet")}</div>
              <div style={{ fontSize: 13, color: P.dyte, lineHeight: 1.5 }}>
                {t("The family code links an account with a profile that already exists — it does not create a new profile. Ask the family administrator to add you to the tree, and then to send you an invitation or a personal code.")}
              </div>
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button onClick={() => setCandidates(null)} style={P.button}>← {t("Search again")}</button>
          </div>
        </div>
      )}
    </KornizaEHyrjes>
  );
}

function Label({ children }) {
  return <div style={{ fontSize: 12.5, color: NGJYRA.dyte, marginBottom: 5 }}>{children}</div>;
}

// FIX (kërkesë e userit — "te Bashkohu me kod / Krijo familje s'punon as back
// button as swipe back"): këto janë faqe TË VEÇANTA (rrugë /join, /start), jo
// modale brenda App.jsx, ndaj s'i mbulonte sistemi i back-ut i pemës. Tani
// çdo gjendje e faqes ka: (1) shigjetë "← Kthehu" lart, (2) swipe djathtas
// për t'u kthyer, (3) butonin fizik Back të Android-it. Kthimi përdor
// history.back() nëse ka histori brenda app-it, përndryshe shkon te "/".

// Korniza e perbashket e faqeve te jashtme (back + swipe + Escape) —
// nje zbatim i vetem, i importuar; jo kopje per cdo faqe.

// ---------------------------------------------------------------------------
// TIPOGRAFIA DHE SIPËRFAQET — pesë rregullat, të zbatuara në një vend
// (1 shtator 2026, pas diskutimit me userin)
//
// Kjo faqe u bë modeli, sepse i shkelte të pesta njëherësh.
//
//  1. Teksti trupor kurrë nën 14px në telefon; dytësori kurrë nën 12.5px.
//  2. Teksti dytësor kurrë më i çelët se `#5F6368` mbi të bardhë (≈5.9:1).
//     Më parë përdoreshin `#888` (≈3.5:1) dhe `#999` (≈2.8:1) — të dyja nën
//     minimumin 4.5:1 për tekst normal.
//  3. Serifi i shfaqjes vetëm për tituj, kurrë nën 16px, kurrë për etiketa.
//  4. Një sipërfaqe që KLIKOHET nuk guxon të ketë të njëjtin kufi e sfond si
//     një fushë ku SHKRUHET.
//  5. Pesha vizuale përputhet me funksionin: një opsion nuk shkruhet kurrë me
//     shkronja fusnote.
//
// Rregulli 4 ishte shkaku i drejtpërdrejtë i ankesës «nuk e kuptoj a është ai
// që kërkova»: `oauthButtonStyle` dhe `inputStyle` kishin TË NJËJTIN kufi
// (`1px solid #d1d5db`) dhe të njëjtin sfond të bardhë. Kandidati dhe fusha e
// kërkimit ishin, fjalë për fjalë, i njëjti objekt vizual.
//
// Zgjidhja: fushat mbeten TË BARDHA me kufi të dukshëm; sipërfaqet që
// klikohen janë TË MBUSHURA. Dallimi është i menjëhershëm dhe nuk varet nga
// leximi.
// ---------------------------------------------------------------------------
const NGJYRA = {
  bosht: "#111827",   // teksti kryesor
  dyte:  "#4B5563",   // dytësori — ≈7.6:1, me shumicë mbi minimumin
  trete: "#5F6368",   // më i çelëti i lejuar — ≈5.9:1
  kufi:  "#D1D5DB",   // kufi fushe
  mbush: "#F3F4F6",   // sipërfaqe që klikohet
  mbushKufi: "#E5E7EB",
  lidhje: "#1D4ED8",  // ≈8.6:1, jo `#2563eb` (≈5.2:1)
  ok:    "#15803D",
};


// Paleta nga tokenat e ballinës (12 shtator 2026): të njëjtat emra si NGJYRA,
// që rregullat 1–5 më sipër të vlejnë pa ndryshim; vetëm vlerat vijnë nga
// varianti që ka zgjedhur admini. Fusha: sfond bg2 me kufi (shkruhet);
// sipërfaqe që klikohet: e mbushur me kufirin e kartelës (rregulli 4).
function paleta(tk) {
  const NG = { bosht: tk.ink, dyte: tk.inkSoft, trete: tk.inkSoft, kufi: tk.cardBorder, mbush: tk.bg2, mbushKufi: tk.cardBorder, lidhje: tk.accent, ok: NGJYRA.ok };
  const input = { padding: "11px 12px", borderRadius: 10, border: `1px solid ${tk.cardBorder}`, background: tk.bg2, fontSize: 15, width: "100%", boxSizing: "border-box", color: tk.ink, fontFamily: "inherit", outline: "none" };
  const button = { padding: "12px 16px", borderRadius: 10, border: "none", background: tk.accent, color: tk.accentInk, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };
  const oauth = { ...button, background: tk.bg2, color: tk.ink, border: `1px solid ${tk.cardBorder}`, fontWeight: 600 };
  return { ...NG, input, button, oauth };
}

// Rrethi me shkronjën e parë — i njëjti model dhe e njëjta paletë si te Posta
// e Admin Center-it, që i njëjti person të ketë të njëjtën ngjyrë kudo në
// produkt. Të dymbëdhjetat janë të errëta sa shkronja e bardhë të lexohet.
const AVATAR = [
  "#B91C1C", "#C2410C", "#B45309", "#4D7C0F", "#15803D", "#0F766E",
  "#0E7490", "#1D4ED8", "#4338CA", "#6D28D9", "#A21CAF", "#BE185D",
];
function ngjyraEAvatarit(celes) {
  const s = String(celes || "?");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR[h % AVATAR.length];
}
function inicialja(s) {
  const v = String(s || "?").trim();
  return (v[0] || "?").toUpperCase();
}

// Kodi e deklaron vetë llojin e vet me formën:
//   6 shifra → familje · `H-…` → mysafir · `P-…` → personal
// Prandaj nuk duhen opsione të fshehura: një fushë e vetme i kupton të tria.
function normalizoKodin(raw) {
  const v = String(raw || "").trim();
  if (/^[hp]/i.test(v)) return v.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 20);
  return v.replace(/\D/g, "").slice(0, 6);
}
function kodiEshteGati(v) {
  const s = String(v || "").trim();
  return /^\d{6}$/.test(s) || /^H-[A-Z0-9]{4,}$/i.test(s) || /^P-[A-Z0-9]{6,}$/i.test(s);
}
